/**
 * Every API call the web app makes, in one file.
 *
 * Thin wrappers around `api` — no caching, no state. React Query owns that;
 * these just describe the endpoints.
 */

import { api, API_PREFIX, clearCsrfToken, query } from './client';
import type {
  CalendarEvent,
  Client,
  Project,
  HandBackResult,
  HandoverKind,
  HandoverResult,
  Invitation,
  InvitationPreview,
  IssuedInvitation,
  RunningTimers,
  Stats,
  Team,
  TeamMember,
  BillingState,
  BoardRead,
  FixedCharge,
  LedgerRead,
  UnfiledStats,
  TimeEntry,
  WorkReport,
  Activity,
  Attachment,
  AuthStatus,
  Comment,
  KbMember,
  KbNote,
  KbNoteSummary,
  KbSecret,
  KbSecretRevealed,
  KbSecretTemplate,
  KbTopic,
  Label,
  Language,
  LabelColor,
  LabelWithCount,
  Notification,
  Paginated,
  TaskCounts,
  TaskCreateInput,
  TaskDetail,
  TaskScheduleInput,
  TaskSummary,
  TaskUpdateInput,
  TodayResponse,
  UpcomingResponse,
  UserMe,
  UserPublic,
  UserSettings,
} from './types';

export interface PageArgs {
  limit?: number;
  offset?: number;
}

// --- Auth -------------------------------------------------------------------

export const auth = {
  status: () => api.get<AuthStatus>('/auth/status'),
  logout: async () => {
    await api.post<void>('/auth/logout');
    // The token belonged to the session that just ended.
    clearCsrfToken();
  },
  logoutAll: async () => {
    const result = await api.post<{ revoked: number }>('/auth/logout-all');
    clearCsrfToken();
    return result;
  },
  /** Mints the double-submit token. The client caches it in memory. */
  csrf: () => api.get<{ csrf_token: string }>('/auth/csrf'),
  /**
   * A full-page navigation, not a fetch — this starts an OAuth redirect, so it
   * must be an absolute URL on the API host when the API lives elsewhere.
   */
  googleLoginUrl: (timezone: string, language?: Language) =>
    `${API_PREFIX}/auth/google/login${query({ client: 'web', timezone, language })}`,
};

// --- Knowledge base -----------------------------------------------------------

export const kb = {
  topics: () => api.get<KbTopic[]>('/kb/topics'),
  createTopic: (name: string) => api.post<KbTopic>('/kb/topics', { name }),
  renameTopic: (id: string, name: string) =>
    api.patch<KbTopic>(`/kb/topics/${id}`, { name }),
  deleteTopic: (id: string) => api.delete(`/kb/topics/${id}`),

  notes: (topicId: string) => api.get<KbNoteSummary[]>(`/kb/topics/${topicId}/notes`),
  note: (id: string, reveal = false) =>
    api.get<KbNote>(`/kb/notes/${id}${reveal ? '?reveal=true' : ''}`),
  createNote: (input: {
    topic_id: string;
    title: string;
    body?: string;
    is_sensitive?: boolean;
  }) => api.post<KbNote>('/kb/notes', input),
  updateNote: (
    id: string,
    input: { title?: string; body?: string; is_sensitive?: boolean },
  ) => api.patch<KbNote>(`/kb/notes/${id}`, input),
  deleteNote: (id: string) => api.delete(`/kb/notes/${id}`),
  revisions: (id: string) =>
    api.get<{ id: string; title: string; body: string; created_at: string }[]>(
      `/kb/notes/${id}/revisions`,
    ),
  search: (q: string, signal?: AbortSignal) =>
    api.get<KbNoteSummary[]>(`/kb/search${query({ q })}`, signal),

  members: (topicId: string) => api.get<KbMember[]>(`/kb/topics/${topicId}/members`),
  shareDisclosure: (topicId: string) =>
    api.get<{ note_count: number; sensitive_count: number }>(
      `/kb/topics/${topicId}/share-disclosure`,
    ),
  share: (topicId: string, userId: string, role: 'VIEWER' | 'EDITOR') =>
    api.post<KbMember>(`/kb/topics/${topicId}/members`, { user_id: userId, role }),
  changeRole: (topicId: string, userId: string, role: 'VIEWER' | 'EDITOR') =>
    api.patch<KbMember>(`/kb/topics/${topicId}/members/${userId}`, { role }),
  revoke: (topicId: string, userId: string) =>
    api.delete(`/kb/topics/${topicId}/members/${userId}`),
  leave: (topicId: string) => api.post(`/kb/topics/${topicId}/leave`),

  notesForTask: (taskId: string) =>
    api.get<{ id: string; title: string; topic_id: string; is_sensitive: boolean }[]>(
      `/tasks/${taskId}/notes`,
    ),
  uploadNoteAttachment: (noteId: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.upload<{
      id: string;
      original_filename: string;
      mime_type: string;
      content_path: string;
    }>(`/kb/notes/${noteId}/attachments`, form);
  },

  secrets: (topicId: string) => api.get<KbSecret[]>(`/kb/topics/${topicId}/secrets`),
  createSecret: (input: {
    topic_id: string;
    name: string;
    fields: Record<string, string>;
  }) => api.post<KbSecret>('/kb/secrets', input),
  /** A POST: every reveal is recorded server-side. */
  revealSecret: (id: string) => api.post<KbSecretRevealed>(`/kb/secrets/${id}/reveal`),
  secretTemplates: () => api.get<KbSecretTemplate[]>('/kb/secret-templates'),
  createSecretTemplate: (input: { name: string; fields: string[] }) =>
    api.post<KbSecretTemplate>('/kb/secret-templates', input),
  updateSecretTemplate: (id: string, input: { name?: string; fields?: string[] }) =>
    api.patch<KbSecretTemplate>(`/kb/secret-templates/${id}`, input),
  deleteSecretTemplate: (id: string) => api.delete(`/kb/secret-templates/${id}`),
  updateSecret: (
    id: string,
    input: { name?: string; fields?: Record<string, string> },
  ) => api.patch<KbSecret>(`/kb/secrets/${id}`, input),
  deleteSecret: (id: string) => api.delete(`/kb/secrets/${id}`),
};

// --- Telegram -----------------------------------------------------------------

export const telegram = {
  userbotStatus: () =>
    api.get<{
      available: boolean;
      connected: boolean;
      pending: boolean;
      /** Where Telegram put the code for a login in progress. */
      delivery?: 'app' | 'sms' | 'call' | null;
      phone: string | null;
    }>('/me/telegram/userbot'),
  /** Omit the phone to send another code to the number already in progress. */
  userbotStart: (phone?: string) =>
    api.post<{ phone: string; delivery?: string }>(
      '/me/telegram/userbot/start',
      phone ? { phone } : {},
    ),
  userbotConfirm: (code: string, password?: string) =>
    api.post<{ phone: string }>('/me/telegram/userbot/confirm', {
      code,
      ...(password ? { password } : {}),
    }),
  userbotDisconnect: () => api.delete('/me/telegram/userbot'),

  /** 404s when the server has no bot configured — the settings screen hides. */
  status: () => api.get<{ linked: boolean; bot_username: string }>('/me/telegram'),
  link: () => api.post<{ url: string }>('/me/telegram/link'),
  unlink: () => api.delete('/me/telegram'),
};

/** A subscribed ICS feed. Its address never comes back — it is a credential. */
export interface CalendarFeed {
  id: string;
  name: string;
  last_synced_at: string | null;
  problem: string | null;
  problem_detail: string | null;
}

export interface CalendarStatus {
  available: boolean;
  connected: boolean;
  account_email: string | null;
  last_synced_at: string | null;
  /** Why the calendar reads as empty; null when it works. */
  problem: string | null;
  /** Google's own sentence about it — often carries the link that fixes it. */
  problem_detail: string | null;
  feeds: CalendarFeed[];
  feeds_available: boolean;
}

/** Projects — what work belongs to, and what it adds up to. */
export const projectsApi = {
  list: (includeArchived = false) =>
    api.get<Project[]>(`/projects${includeArchived ? '?include_archived=true' : ''}`),
  get: (id: string) => api.get<Project>(`/projects/${id}`),
  unfiled: () => api.get<UnfiledStats>('/projects/unfiled'),
  create: (input: { name: string; color?: string; client_id?: string; due_date?: string }) =>
    api.post<Project>('/projects', input),
  update: (id: string, patch: Partial<Project> & { archived?: boolean }) =>
    api.patch<Project>(`/projects/${id}`, patch),
  remove: (id: string) => api.delete(`/projects/${id}`),
};

/** Teams and invitations — who is us, and how somebody becomes us. */
export const teamsApi = {
  list: (includeArchived = false) =>
    api.get<Team[]>(`/teams${includeArchived ? '?include_archived=true' : ''}`),
  create: (input: { name: string; description?: string }) =>
    api.post<Team>('/teams', input),
  update: (id: string, patch: { name?: string; description?: string; archived?: boolean }) =>
    api.patch<Team>(`/teams/${id}`, patch),
  remove: (id: string) => api.delete(`/teams/${id}`),

  members: (id: string) => api.get<TeamMember[]>(`/teams/${id}/members`),
  setRole: (id: string, userId: string, role: 'MANAGER' | 'MEMBER') =>
    api.put<TeamMember>(`/teams/${id}/members/${userId}`, { role }),
  removeMember: (id: string, userId: string) =>
    api.delete(`/teams/${id}/members/${userId}`),

  invitations: (teamId?: string) =>
    api.get<Invitation[]>(`/invitations${teamId ? `?team_id=${teamId}` : ''}`),
  invite: (input: {
    email: string;
    team_id?: string | null;
    role?: 'MANAGER' | 'MEMBER';
    ttl_days?: number;
  }) => api.post<IssuedInvitation>('/invitations', input),
  revokeInvitation: (id: string) => api.delete<Invitation>(`/invitations/${id}`),
  acceptInvitation: (token: string) =>
    api.post<Invitation>('/invitations/accept', { token }),
  addMember: (id: string, userId: string, role: 'MANAGER' | 'MEMBER' = 'MEMBER') =>
    api.post<TeamMember>(`/teams/${id}/members`, { user_id: userId, role }),
  //. Unauthenticated: the recipient has no account yet.
  previewInvitation: (token: string) =>
    api.get<InvitationPreview>(`/invitations/preview/${encodeURIComponent(token)}`),
};

/** Statistics — how much, how long, how many. Never what. */
export const statsApi = {
  get: (scope: 'me' | 'team', days: number, teamId?: string | null) =>
    api.get<Stats>(`/stats${query({ scope, days, team_id: teamId ?? undefined })}`),
};

/** Clients, the timer, and work reports — all the caller's own. */
export const billingApi = {
  clients: (includeArchived = false) =>
    api.get<Client[]>(`/clients${includeArchived ? '?include_archived=true' : ''}`),
  createClient: (input: { name: string; hourly_rate: string; currency?: string }) =>
    api.post<Client>('/clients', input),
  updateClient: (id: string, patch: Partial<Client> & { archived?: boolean }) =>
    api.patch<Client>(`/clients/${id}`, patch),
  deleteClient: (id: string) => api.delete(`/clients/${id}`),

  timer: () => api.get<RunningTimers>('/me/timer'),
  startTimer: (taskId: string, parallel = false) =>
    api.post<RunningTimers>(`/tasks/${taskId}/timer/start`, { parallel }),
  stopTimer: (taskId?: string) =>
    api.post<RunningTimers>('/me/timer/stop', taskId ? { task_id: taskId } : {}),

  taskEntries: (taskId: string) => api.get<TimeEntry[]>(`/tasks/${taskId}/time-entries`),
  addEntry: (input: { task_id: string; started_at: string; ended_at: string; note?: string }) =>
    api.post<TimeEntry>('/time-entries', input),
  updateEntry: (
    id: string,
    patch: { started_at?: string; ended_at?: string; note?: string },
  ) => api.patch<TimeEntry>(`/time-entries/${id}`, patch),
  deleteEntry: (id: string) => api.delete(`/time-entries/${id}`),

  unbilled: (clientId: string, from: string, to: string) =>
    api.get<{
      minutes: number;
      /** The exact total the protocol would freeze — computed server-side. */
      amount: string;
      entries: TimeEntry[];
      /** Quoted jobs, billed once each — they have no minutes. */
      fixed_charges: FixedCharge[];
      task_titles: Record<string, string>;
      /** Entry id → the billing day in the user's timezone. */
      entry_days: Record<string, string>;
    }>(`/clients/${clientId}/unbilled?period_start=${from}&period_end=${to}`),
  /**
   * The ledger behind both the client screen and a project's breakdown.
   * Everything is optional: no filter means every billable item there is.
   */
  ledger: (filter: {
    client_id?: string;
    project_id?: string;
    state?: BillingState[];
    period_start?: string;
    period_end?: string;
  } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) {
      if (Array.isArray(value)) value.forEach((one) => query.append(key, one));
      else if (value) query.set(key, value);
    }
    const suffix = query.toString();
    return api.get<LedgerRead>(`/billing/ledger${suffix ? `?${suffix}` : ''}`);
  },
  /**
   * Say what really happened to the selected items. With `paid_amount` this
   * records a payment of that size against them — spread oldest first — rather
   * than declaring all of them paid.
   */
  setLedgerState: (input: {
    entry_ids?: string[];
    task_ids?: string[];
    state: BillingState;
    paid_amount?: string;
  }) => api.patch<{ updated: number }>('/billing/ledger', input),
  issueReport: (
    clientId: string,
    from: string,
    to: string,
    selection?: { entry_ids?: string[]; task_ids?: string[] },
  ) =>
    api.post<WorkReport>(`/clients/${clientId}/reports`, {
      ...selection,
      period_start: from,
      period_end: to,
    }),
  unbilledOverview: () =>
    api.get<
      { client_id: string; client_name: string; currency: string; minutes: number; amount: string }[]
    >('/me/unbilled'),
  voidReport: (id: string) => api.delete(`/reports/${id}`),
  markReportPaid: (id: string, paid: boolean) =>
    api.patch<WorkReport>(`/reports/${id}`, { paid }),
  reports: (clientId?: string) =>
    api.get<WorkReport[]>(`/reports${clientId ? `?client_id=${clientId}` : ''}`),
  report: (id: string) => api.get<WorkReport>(`/reports/${id}`),
};

/** Calendars — reading the day someone already has. Never writing to any of them. */
export const calendarApi = {
  status: () => api.get<CalendarStatus>('/me/calendar'),
  events: (date?: string) =>
    api.get<CalendarEvent[]>(`/me/calendar/events${query({ date })}`),
  disconnect: () => api.delete('/me/calendar'),
  addFeed: (url: string, name?: string) =>
    api.post<CalendarFeed>('/me/calendar/feeds', { url, name: name || undefined }),
  removeFeed: (id: string) => api.delete(`/me/calendar/feeds/${id}`),
};

// --- Me ---------------------------------------------------------------------

export const me = {
  get: () => api.get<UserMe>('/me'),
  update: (body: { display_name?: string; first_name?: string; last_name?: string }) =>
    api.patch<UserMe>('/me', body),
  settings: () => api.get<UserSettings>('/me/settings'),
  updateSettings: (body: Partial<UserSettings>) =>
    api.patch<UserSettings>('/me/settings', body),
  usernameSuggestion: () =>
    api.get<{ suggested: string; alternatives: string[] }>('/me/username/suggestion'),
  setUsername: (username: string) => api.put<UserMe>('/me/username', { username }),
};

// --- Users ------------------------------------------------------------------

export const users = {
  /** Backs the `@` autocomplete. Minimum two characters, capped server-side. */
  search: (q: string, signal?: AbortSignal) =>
    api.get<UserPublic[]>(`/users/search${query({ q, limit: 10 })}`, signal),
  usernameAvailable: (username: string) =>
    api.get<{ username: string; available: boolean; reason: string | null }>(
      `/users/username-available${query({ username })}`,
    ),
  byUsername: (username: string) => api.get<UserPublic>(`/users/${username}`),
};

// --- Labels -----------------------------------------------------------------

export const labels = {
  list: () => api.get<LabelWithCount[]>('/labels'),
  create: (body: { name: string; color?: LabelColor }) =>
    api.post<Label>('/labels', body),
  update: (
    id: string,
    body: { name?: string; color?: LabelColor; sort_order?: number },
  ) => api.patch<Label>(`/labels/${id}`, body),
  remove: (id: string) => api.delete<void>(`/labels/${id}`),
  /** Replace *your* labels on a task; other people's filing is untouched. */
  setOnTask: (taskId: string, labelIds: string[]) =>
    api.put<TaskDetail>(`/tasks/${taskId}/labels`, { label_ids: labelIds }),
};

// --- Tasks ------------------------------------------------------------------

export const tasks = {
  today: () => api.get<TodayResponse>('/tasks/today'),
  upcoming: (days?: number) =>
    api.get<UpcomingResponse>(`/tasks/upcoming${query({ days })}`),
  backlog: (page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks/backlog${query({ ...page })}`),
  /** `q` searches the whole archive; without it the list covers recent days. */
  completed: (page: PageArgs = {}, q?: string) =>
    api.get<Paginated<TaskSummary>>(`/tasks/completed${query({ ...page, q })}`),
  assignedToMe: (page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks/assigned-to-me${query({ ...page })}`),
  handOver: (
    taskId: string,
    input: {
      assigned_to_user_id: string;
      kind: HandoverKind;
      note: string;
      due_date?: string | null;
    },
  ) => api.post<HandoverResult>(`/tasks/${taskId}/handover`, input),
  handBack: (taskId: string, note: string) =>
    api.post<HandBackResult>(`/tasks/${taskId}/handback`, { note }),
  involved: (page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks/involved${query({ ...page })}`),
  assignedByMe: (page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks/assigned-by-me${query({ ...page })}`),
  counts: () => api.get<TaskCounts>('/tasks/counts'),
  search: (q: string, page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks/search${query({ q, ...page })}`),
  byLabel: (labelId: string, page: PageArgs = {}) =>
    api.get<Paginated<TaskSummary>>(`/tasks${query({ label_id: labelId, ...page })}`),

  get: (id: string) => api.get<TaskDetail>(`/tasks/${id}`),
  create: (body: TaskCreateInput) => api.post<TaskDetail>('/tasks', body),
  update: (id: string, body: TaskUpdateInput) =>
    api.patch<TaskDetail>(`/tasks/${id}`, body),
  remove: (id: string) => api.delete<void>(`/tasks/${id}`),
  restore: (id: string) => api.post<TaskDetail>(`/tasks/${id}/restore`),

  /** The same tasks as every list, arranged by the stage they are in. */
  board: (projectId?: string) =>
    api.get<BoardRead>(`/tasks/board${projectId ? `?project_id=${projectId}` : ''}`),

  complete: (id: string) => api.post<TaskDetail>(`/tasks/${id}/complete`),
  reopen: (id: string) => api.post<TaskDetail>(`/tasks/${id}/reopen`),
  schedule: (id: string, body: TaskScheduleInput) =>
    api.post<TaskDetail>(`/tasks/${id}/schedule`, body),
  /** File a task under another one, or promote it back. Null frees it. */
  setParent: (id: string, parentTaskId: string | null) =>
    api.post<TaskDetail>(`/tasks/${id}/parent`, { parent_task_id: parentTaskId }),
  /** Delegation always travels as an explicit id, never as text in the title. */
  assign: (id: string, assignedToUserId: string) =>
    api.post<TaskDetail>(`/tasks/${id}/assign`, {
      assigned_to_user_id: assignedToUserId,
    }),
};

// --- Task sub-resources -----------------------------------------------------

export const attachments = {
  list: (taskId: string) => api.get<Attachment[]>(`/tasks/${taskId}/attachments`),
  upload: (taskId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<Attachment>(`/tasks/${taskId}/attachments`, form);
  },
  remove: (taskId: string, attachmentId: string) =>
    api.delete<void>(`/tasks/${taskId}/attachments/${attachmentId}`),
  /**
   * Streamed through the API after an authorization check — never a bucket URL.
   *
   * Used as an `<img src>` / link target, so it needs the API origin. Note that
   * an `<img>` does not send credentials cross-origin unless it carries
   * `crossorigin="use-credentials"`; the components that render attachments
   * must set that.
   */
  contentUrl: (taskId: string, attachmentId: string) =>
    `${API_PREFIX}/tasks/${taskId}/attachments/${attachmentId}/content`,
};

export const comments = {
  list: (taskId: string) => api.get<Comment[]>(`/tasks/${taskId}/comments`),
  create: (taskId: string, body: string) =>
    api.post<Comment>(`/tasks/${taskId}/comments`, { body }),
  update: (taskId: string, commentId: string, body: string) =>
    api.patch<Comment>(`/tasks/${taskId}/comments/${commentId}`, { body }),
  remove: (taskId: string, commentId: string) =>
    api.delete<void>(`/tasks/${taskId}/comments/${commentId}`),
};

export const activity = {
  list: (taskId: string, page: PageArgs = {}) =>
    api.get<Paginated<Activity>>(`/tasks/${taskId}/activity${query({ ...page })}`),
};

export const notifications = {
  list: (page: PageArgs & { unread_only?: boolean } = {}) =>
    api.get<Paginated<Notification>>(`/notifications${query({ ...page })}`),
  unreadCount: () => api.get<{ unread: number }>('/notifications/unread-count'),
  markRead: (id: string) => api.post<Notification>(`/notifications/${id}/read`),
  markAllRead: () => api.post<{ unread: number }>('/notifications/read-all'),
};
